/**
 * 遊ばれ方の記録（自前の軽い計測）の形。
 *
 * 画面側（src/meta/telemetry.ts）とサーバー側（functions/api/events.ts）が同じ表で検査する。
 * 名前・メール・広告ID・端末の識別子（IDFA など）は**どこにも入れない**。
 * 端末ID はランキングと同じ、端末の中で作ったランダムな文字列だけ。
 */

/** 送る出来事の種類。増やすときはここと docs/計測の見方.md に足す。 */
export const TELEMETRY_EVENT_TYPES = [
  /**
   * 起動（タブ・アプリを開いたとき。同じタブの中で県を移っても1回）。
   * アプリを閉じずに翌日また前面に出したとき（日本時間で日が変わっていたら）も1回（detail=resume）。
   * Web 版で URL に utm が付いていれば detail=<utm_source>.<utm_medium>（例: share.x）。
   */
  'launch',
  /** この端末で初めてのプレイ開始。 */
  'first_play',
  /** プレイ開始（region / mode）。 */
  'play_start',
  /** プレイ終了（region / mode / level / drops / play_ms / score / continues / items、detail=over|quit）。 */
  'play_end',
  /** 合間の広告が出た。 */
  'ad_interstitial',
  /** ごほうび広告を最後まで見た（detail=continue|item|rescue|rest）。 */
  'ad_rewarded',
  /** ごほうび広告をすすめるボタンを出した（detail=continue：結果画面の「広告を見て続ける」｜ohashi：ショップの「広告を見て1本もらう」｜rescue：「こぼれそう！」のときの「広告でおはし」）。 */
  'ad_offer',
  /** iOS のトラッキング許可（ATT）の結果（detail=authorized|denied|restricted|notDetermined）。ダイアログを出したときと、制限されていて出せなかったとき。 */
  'att',
  /** ショップを開いた（detail=開いた場所：settings|result|ohashi_button|other）。 */
  'shop_open',
  /** ショップを開いた時点で、ストアの値段が取れていなかった（detail=取り直しの結果：ok|ng）。 */
  'price_missing',
  /** 購入が成立した（detail=商品ID）。 */
  'purchase',
  /** 購入を自分でやめた（detail=商品ID）。 */
  'purchase_cancel',
  /** 購入に失敗した（detail=商品ID）。 */
  'purchase_fail',
  /** 共有（detail=手段：native|x|line|copy|image|image_share|video。画像は載せた名場面つき：image_share.level|image_share.combo|image_share.final など）。 */
  'share',
  /** 豆知識を開いた。 */
  'tip_open',
  /** 「食べに行く」を押した。 */
  'eat_out',
  /** 今日の県のお知らせの許可（detail=granted|denied）。 */
  'notice_permission',
  /** お知らせをタップしてアプリを開いた。 */
  'notice_open',
  /** 連続日数のごほうびを渡した（detail=何日目。例: 7）。 */
  'streak_reward',
  /** ストアのレビュー依頼を出した（実際に表示するかは OS が決める）。 */
  'review_request',
] as const;

export type TelemetryEventType = (typeof TELEMETRY_EVENT_TYPES)[number];

export const TELEMETRY_MODES = ['normal', 'daily', 'relaxed'] as const;
export type TelemetryMode = (typeof TELEMETRY_MODES)[number];

export const TELEMETRY_PLATFORMS = ['web', 'ios', 'android'] as const;
export type TelemetryPlatform = (typeof TELEMETRY_PLATFORMS)[number];

/** 1件の出来事。数値はプレイの記録のときだけ入る。 */
export interface TelemetryEvent {
  type: TelemetryEventType;
  /** 起きた時刻（ミリ秒）。 */
  at: number;
  region?: string;
  mode?: TelemetryMode;
  level?: number;
  drops?: number;
  playMs?: number;
  score?: number;
  continues?: number;
  items?: number;
  detail?: string;
}

export interface TelemetryBatch {
  device: string;
  platform: TelemetryPlatform;
  app: string;
  events: TelemetryEvent[];
}

/** 1回の送信でまとめて送る最大件数（サーバーもこれを超えたら受け取らない）。 */
export const TELEMETRY_MAX_BATCH = 50;
/** 1回の送信の大きさの上限（バイト）。 */
export const TELEMETRY_MAX_BODY_BYTES = 32_000;

const NUMBER_CAPS = {
  level: 12,
  drops: 100_000,
  playMs: 24 * 3600_000,
  score: 10_000_000,
  continues: 100,
  items: 100,
} as const;

export const isTelemetryDevice = (v: unknown): v is string => typeof v === 'string' && /^[a-z0-9-]{8,40}$/.test(v);
const isApp = (v: unknown): v is string => typeof v === 'string' && /^[0-9A-Za-z.+_-]{1,40}$/.test(v);
/** 補足の文字。英数字と _ . - だけ（ATT の notDetermined のため大文字も通す）。 */
const isDetail = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z0-9_.-]{1,40}$/.test(v);

export type BatchCheck =
  | { ok: true; batch: TelemetryBatch }
  | { ok: false; error: string };

/**
 * 送られてきた中身を検査する。おかしい出来事が1件でもあれば全体を受け取らない
 * （画面側のバグにすぐ気づけるように。画面側は同じ表で作るので、ふつうは起きない）。
 *
 * @param regions 受け付ける県ID（region を付ける出来事のため）
 * @param now     サーバーの時刻。時刻が大きくずれた出来事ははじく
 */
export function checkTelemetryBatch(
  raw: unknown,
  regions: ReadonlySet<string> | readonly string[],
  now: number = Date.now(),
): BatchCheck {
  const regionSet = regions instanceof Set ? regions : new Set(regions as readonly string[]);
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'bad-body' };
  const body = raw as Record<string, unknown>;
  if (!isTelemetryDevice(body.device)) return { ok: false, error: 'bad-device' };
  if (!TELEMETRY_PLATFORMS.includes(body.platform as TelemetryPlatform)) return { ok: false, error: 'bad-platform' };
  if (!isApp(body.app)) return { ok: false, error: 'bad-app' };
  if (!Array.isArray(body.events)) return { ok: false, error: 'bad-events' };
  if (body.events.length === 0 || body.events.length > TELEMETRY_MAX_BATCH) return { ok: false, error: 'bad-count' };

  const events: TelemetryEvent[] = [];
  // 端末の時計のずれと、オフラインでためた分（数日）を許す
  const oldest = now - 7 * 86_400_000;
  const newest = now + 86_400_000;
  for (const item of body.events) {
    if (!item || typeof item !== 'object') return { ok: false, error: 'bad-event' };
    const e = item as Record<string, unknown>;
    if (!TELEMETRY_EVENT_TYPES.includes(e.type as TelemetryEventType)) return { ok: false, error: 'bad-type' };
    if (typeof e.at !== 'number' || !Number.isFinite(e.at) || e.at < oldest || e.at > newest) {
      return { ok: false, error: 'bad-time' };
    }
    const out: TelemetryEvent = { type: e.type as TelemetryEventType, at: Math.round(e.at) };
    if (e.region !== undefined) {
      if (typeof e.region !== 'string' || !regionSet.has(e.region)) return { ok: false, error: 'bad-region' };
      out.region = e.region;
    }
    if (e.mode !== undefined) {
      if (!TELEMETRY_MODES.includes(e.mode as TelemetryMode)) return { ok: false, error: 'bad-mode' };
      out.mode = e.mode as TelemetryMode;
    }
    for (const key of Object.keys(NUMBER_CAPS) as Array<keyof typeof NUMBER_CAPS>) {
      const v = e[key];
      if (v === undefined) continue;
      if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > NUMBER_CAPS[key]) {
        return { ok: false, error: `bad-${key}` };
      }
      out[key] = v;
    }
    if (e.detail !== undefined) {
      if (!isDetail(e.detail)) return { ok: false, error: 'bad-detail' };
      out.detail = e.detail;
    }
    events.push(out);
  }
  return {
    ok: true,
    batch: {
      device: body.device,
      platform: body.platform as TelemetryPlatform,
      app: body.app,
      events,
    },
  };
}

/** 日本時間の日付（集計の「日」）。 */
export function jstDay(at: number): string {
  return new Date(at + 9 * 3600_000).toISOString().slice(0, 10);
}
